"use client";

import { UiLabelsProvider } from "@v1/ui/ui-labels";
import type { AbstractIntlMessages } from "next-intl";
import { NextIntlClientProvider, useTranslations } from "next-intl";
import { type ReactElement, type ReactNode, useMemo } from "react";
import type { Locale } from "./locales";
import { humanizeMessageKey } from "./message-fallback";

/** Client intl context for a given catalog; readable fallback for missing keys. */
export function IntlMessagesProvider({
  locale,
  timeZone,
  messages,
  children,
}: {
  readonly locale: Locale;
  readonly timeZone: string;
  readonly messages: AbstractIntlMessages;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <NextIntlClientProvider
      locale={locale}
      messages={messages}
      timeZone={timeZone}
      getMessageFallback={humanizeMessageKey}
    >
      <UiLabelsBridge>{children}</UiLabelsBridge>
    </NextIntlClientProvider>
  );
}

/** Translated screen-reader labels for `@v1/ui` chrome (dialog/sheet close X). */
function UiLabelsBridge({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const t = useTranslations("common.actions");
  const labels = useMemo(() => ({ close: t("close") }), [t]);
  return <UiLabelsProvider value={labels}>{children}</UiLabelsProvider>;
}
